'use client';
import React, { useState } from 'react';
import { QrCode, ArrowRightLeft, Eye, ChevronUp, ChevronDown } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { GravePlot } from './GravePlotContent';

interface Props {
  plots: GravePlot[];
  onQR: (p: GravePlot) => void;
  onTransfer: (p: GravePlot) => void;
}

type SortKey = keyof GravePlot;

export default function PlotTable({ plots, onQR, onTransfer }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('plotId');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const perPage = 10;

  const sorted = [...plots].sort((a, b) =>
    sortDir === 'asc'
      ? String(a[sortKey]).localeCompare(String(b[sortKey]))
      : String(b[sortKey]).localeCompare(String(a[sortKey]))
  );

  const totalPages = Math.ceil(sorted.length / perPage);
  const paged = sorted.slice((page - 1) * perPage, page * perPage);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: SortKey }) => (
    <span className="inline-flex flex-col ml-1">
      <ChevronUp size={10} className={sortKey === col && sortDir === 'asc' ? 'text-primary' : 'text-muted-foreground/40'} />
      <ChevronDown size={10} className={sortKey === col && sortDir === 'desc' ? 'text-primary' : 'text-muted-foreground/40'} />
    </span>
  );

  return (
    <div className="card-base overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              {([
                ['plotId', 'Plot ID'],
                ['cemetery', 'Cemetery'],
                ['block', 'Block'],
                ['row', 'Row'],
                ['plotNumber', 'Plot No.'],
                ['gpsLat', 'GPS'],
                ['status', 'Status'],
                ['deceasedName', 'Deceased'],
                ['burialDate', 'Burial Date'],
                ['ownerName', 'Owner/Beneficiary'],
              ] as [SortKey, string][]).map(([key, label]) => (
                <th
                  key={`th-plot-${key}`}
                  onClick={() => toggleSort(key)}
                  className="px-3 py-3 text-left text-[12px] font-600 text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground whitespace-nowrap"
                >
                  {label} <SortIcon col={key} />
                </th>
              ))}
              <th className="px-3 py-3 text-right text-[12px] font-600 text-muted-foreground uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paged.map((plot) => (
              <tr key={plot.id} className="table-row-hover transition-colors">
                <td className="px-3 py-3">
                  <span className="font-mono-data text-xs font-600 text-primary">{plot.plotId}</span>
                </td>
                <td className="px-3 py-3 text-muted-foreground">{plot.cemetery}</td>
                <td className="px-3 py-3">
                  <span className="font-mono-data text-xs font-600">{plot.block}</span>
                </td>
                <td className="px-3 py-3">
                  <span className="font-mono-data text-xs">{plot.row}</span>
                </td>
                <td className="px-3 py-3">
                  <span className="font-mono-data text-xs font-600">{plot.plotNumber}</span>
                </td>
                <td className="px-3 py-3">
                  <span className="font-mono-data text-xs text-muted-foreground">{plot.gpsLat}, {plot.gpsLng}</span>
                </td>
                <td className="px-3 py-3">
                  <StatusBadge status={plot.status} size="sm" />
                </td>
                <td className="px-3 py-3 text-foreground font-500 whitespace-nowrap">
                  {plot.deceasedName === '—' ? <span className="text-muted-foreground">—</span> : plot.deceasedName}
                </td>
                <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{plot.burialDate}</td>
                <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">
                  {plot.ownerName === '—' ? '—' : plot.ownerName}
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onQR(plot)}
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-muted transition-colors"
                      title="View QR Code"
                    >
                      <QrCode size={14} className="text-muted-foreground" />
                    </button>
                    {plot.status === 'occupied' && (
                      <button
                        onClick={() => onTransfer(plot)}
                        className="w-7 h-7 flex items-center justify-center rounded hover:bg-muted transition-colors"
                        title="Transfer Plot Ownership"
                      >
                        <ArrowRightLeft size={14} className="text-muted-foreground" />
                      </button>
                    )}
                    <button
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-muted transition-colors"
                      title="View Plot Details"
                    >
                      <Eye size={14} className="text-muted-foreground" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
        <span className="text-xs text-muted-foreground">
          Showing {Math.min((page - 1) * perPage + 1, sorted.length)}–{Math.min(page * perPage, sorted.length)} of {sorted.length} plots
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-2 py-1 text-xs rounded border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={`plot-page-${i + 1}`}
              onClick={() => setPage(i + 1)}
              className={`w-7 h-7 text-xs rounded border transition-colors ${
                page === i + 1 ? 'bg-primary text-white border-primary' : 'border-border hover:bg-muted'
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="px-2 py-1 text-xs rounded border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}